import React, { useCallback, useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { $createCodeNode } from '@lexical/code';
import { EditorState, $getRoot, $createTextNode } from 'lexical';
import { registerCodeHighlighting } from '@lexical/code';

// Lua syntax highlighting theme for both light and dark modes
const codeTheme = {
  code: 'font-mono text-sm p-4 rounded-md overflow-auto',
  codeHighlight: {
    atrule: 'text-indigo-600 dark:text-indigo-400',
    attr: 'text-cyan-600 dark:text-cyan-400',
    boolean: 'text-orange-600 dark:text-orange-400',
    builtin: 'text-indigo-600 dark:text-indigo-400',
    cdata: 'text-gray-500 dark:text-gray-400',
    char: 'text-green-600 dark:text-green-400',
    class: 'text-yellow-600 dark:text-yellow-400',
    'class-name': 'text-yellow-600 dark:text-yellow-400',
    comment: 'text-gray-500 dark:text-gray-500 italic',
    constant: 'text-orange-600 dark:text-orange-400',
    deleted: 'text-red-600 dark:text-red-400',
    doctype: 'text-gray-500 dark:text-gray-400',
    entity: 'text-orange-600 dark:text-orange-400',
    function: 'text-blue-600 dark:text-blue-400',
    important: 'text-red-600 dark:text-red-400',
    inserted: 'text-green-600 dark:text-green-400',
    keyword: 'text-purple-600 dark:text-purple-400 font-semibold',
    namespace: 'text-yellow-600 dark:text-yellow-400',
    number: 'text-orange-600 dark:text-orange-400',
    operator: 'text-gray-700 dark:text-gray-300',
    prolog: 'text-gray-500 dark:text-gray-400',
    property: 'text-cyan-600 dark:text-cyan-400',
    punctuation: 'text-gray-700 dark:text-gray-300',
    regex: 'text-green-600 dark:text-green-400',
    selector: 'text-yellow-600 dark:text-yellow-400',
    string: 'text-green-600 dark:text-green-400',
    symbol: 'text-orange-600 dark:text-orange-400',
    tag: 'text-red-600 dark:text-red-400',
    url: 'text-blue-600 dark:text-blue-400 underline',
    variable: 'text-blue-700 dark:text-blue-300',
  },
};

const theme = {
  ...codeTheme,
  paragraph: 'mb-0',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
};

const initialConfig = {
  namespace: 'LuaCodeEditor',
  theme,
  onError: (error: Error) => {
    console.error('Lexical Lua editor error:', error);
  },
  nodes: [
    CodeNode,
    CodeHighlightNode,
  ],
};

interface LuaCodeEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  rows?: number;
  showLineNumbers?: boolean;
}

const EditorUpdater = ({ value }: { value: string }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const editorState = editor.getEditorState();
    const currentText = editorState.read(() => $getRoot().getTextContent());
    if (currentText !== value) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const codeNode = $createCodeNode('yaml'); // Change to 'yaml'
        const textNode = $createTextNode(value);
        codeNode.append(textNode);
        root.append(codeNode);
      });
    }
  }, [value, editor]);

  useEffect(() => {
    return registerCodeHighlighting(editor);
  }, [editor]);

  return null;
 };

const LuaCodeEditor: React.FC<LuaCodeEditorProps> = ({
  value = '',
  onChange,
  placeholder = '-- Enter Lua code here...',
  disabled = false,
  className = '',
  rows = 8,
  showLineNumbers = true,
}) => {

  const handleChange = useCallback((editorState: EditorState) => {
    if (onChange) {
      editorState.read(() => {
        const root = $getRoot();
        const textContent = root.getTextContent();
        onChange(textContent);
      });
    }
  }, [onChange]);

  // Initialize editor with plain text content
  const getInitialEditorState = useCallback(() => {
    if (!value || value.trim() === '') {
      return undefined;
    }
    
    return () => {
      const root = $getRoot();
      const codeNode = $createCodeNode('lua');
      const textNode = $createTextNode(value);
      codeNode.append(textNode);
      root.append(codeNode);
    };
  }, [value]);

  const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    return <div>{children}</div>;
  };

  // Calculate height based on rows
  const minHeight = `${rows * 1.5}rem`;

  // Generate line numbers
  const lineCount = value ? value.split('\n').length : 1;
  const lineNumbers = Array.from({ length: Math.max(lineCount, rows) }, (_, i) => i + 1);

  return (
    <LexicalComposer 
      initialConfig={{
        ...initialConfig,
        editorState: getInitialEditorState(),
        editable: !disabled,
      }}
    >
      <div className={`relative border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden ${className}`}>
        <div className="flex bg-gray-50 dark:bg-gray-900">
          {showLineNumbers && (
            <div className="flex-shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs font-mono leading-6 p-4 pr-2 select-none border-r border-gray-200 dark:border-gray-700">
              {lineNumbers.map(num => (
                <div key={num} className="text-right">
                  {num}
                </div>
              ))}
            </div>
          )}
          <div className="flex-1 relative">
            <PlainTextPlugin
              contentEditable={
                <ContentEditable
                  className={`
                    bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm p-4 
                    focus:outline-none resize-none overflow-auto whitespace-pre
                    ${disabled ? 'cursor-not-allowed opacity-60' : ''}
                  `}
                  style={{ 
                    minHeight,
                    userSelect: disabled ? 'none' : 'auto',
                    pointerEvents: disabled ? 'none' : 'auto',
                    lineHeight: '1.5',
                  }}
                />
              }
              placeholder={
                <div 
                  className="absolute top-4 left-4 text-gray-400 dark:text-gray-500 pointer-events-none select-none font-mono text-sm"
                  style={{ lineHeight: '1.5' }}
                >
                  {placeholder}
                </div>
              }
              ErrorBoundary={ErrorBoundary}
            />
          </div>
        </div>
        <OnChangePlugin onChange={handleChange} />
        <HistoryPlugin />
        <EditorUpdater value={value} />
      </div>
    </LexicalComposer>
  );
};

export default LuaCodeEditor;
